import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PublicShell from '../components/PublicShell';
import { Home, Shield, ShieldCheck, FileText, BookOpen, Database, LogOut, LogIn, DollarSign } from 'lucide-react';
import axios from 'axios';
import LeadCaptureCTA from '../components/LeadCaptureCTA';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAV = [
  { path: '/', icon: Home, label: 'Home', color: '#0ea5e9' },
  { path: '/platform', icon: Shield, label: 'Intelligence Platform', auth: true, color: '#16a34a' },
  { path: '/documentation', icon: FileText, label: 'Documentation', color: '#2563eb' },
  { path: '/use-cases', icon: BookOpen, label: 'Use Cases', color: '#9333ea' },
  { path: '/sources', icon: Database, label: 'Research Database', color: '#0891b2' },
  { path: '/provenance', icon: ShieldCheck, label: 'Data Provenance', color: '#059669' },
  { path: '/pricing', icon: DollarSign, label: 'Pricing', color: '#ca8a04' },
];

export default function SourcesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const go = (path, auth) => navigate(auth && !user ? '/login' : path);

  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);
  const [apiOnly, setApiOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  useEffect(() => {
    Promise.all([axios.get(`${API}/sources/stats`), axios.get(`${API}/sources`)])
      .then(([s, d]) => { setStats(s.data); setSources(d.data); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = {};
    if (selectedCat) params.category = selectedCat;
    if (apiOnly) params.api_only = true;
    if (search) params.search = search;
    axios.get(`${API}/sources`, { params }).then(res => setSources(res.data));
  }, [selectedCat, apiOnly, search]);

  const runHealthCheck = () => {
    setHealthLoading(true);
    axios.get(`${API}/sources/health`, { timeout: 120000 })
      .then(res => setHealthData(res.data))
      .catch(() => {})
      .finally(() => setHealthLoading(false));
  };

  const priorityColor = (p) => p === 'Critical' ? 'bg-red-50 text-red-600' : p === 'High' ? 'bg-amber-50 text-amber-600' : p === 'Medium' ? 'bg-blue-50 text-blue-600' : 'bg-[#f8f9ff] text-[#434655]';
  const apiColor = (a) => (a === 'Yes' || a === 'API') ? 'bg-green-50 text-green-600' : a === 'Partial' ? 'bg-yellow-50 text-yellow-600' : 'bg-[#f8f9ff] text-[#434655]';
  const statusDot = (s) => s === 'online' ? 'bg-green-500' : s === 'restricted' ? 'bg-yellow-500' : s === 'timeout' ? 'bg-orange-500' : 'bg-red-500';

  return (
    <PublicShell user={user} onLogout={onLogout}>
      <div className="overflow-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
        {/* Dynamic kinetic hero */}
        <style>{`
          @keyframes slowSpinSrc { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .animate-slowSpinSrc { animation: slowSpinSrc 40s linear infinite; }
          .grid-bg-src { background-image: linear-gradient(rgba(0,74,198,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,74,198,0.05) 1px, transparent 1px); background-size: 40px 40px; }
        `}</style>
        <section className="relative overflow-hidden bg-gradient-to-br from-[#f8f9ff] via-white to-[#eff4ff] border-b border-[#c3c6d7]/20">
          <div className="absolute inset-0 grid-bg-src opacity-60 pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-gradient-to-br from-[#004ac6]/5 via-[#7c3aed]/5 to-transparent animate-slowSpinSrc pointer-events-none" />
          <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
            <div className="inline-flex items-center gap-2 bg-white border border-[#c3c6d7]/40 rounded-full px-3 py-1 mb-4 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#434655]">1,000+ sources · 62 live APIs</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-[#0b1c30] leading-[1.05] mb-3">
              Research <span className="bg-gradient-to-r from-[#004ac6] to-[#7c3aed] bg-clip-text text-transparent">Database</span>
            </h1>
            <p className="text-sm text-[#434655] max-w-2xl">
              Every data feed, regulator, trade body, and market provider powering Sovereign IQ — curated, categorized, and health-monitored in real time.
            </p>
          </div>
        </section>

        <div className="p-6 lg:p-8">
          <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
            <div className="relative ml-auto">
              <input className="bg-white border border-[#c3c6d7]/40 rounded-lg py-2 pl-3 pr-4 text-xs w-64 focus:ring-1 focus:ring-[#004ac6] outline-none"
                placeholder="Search sources..." value={search} onChange={e => setSearch(e.target.value)} data-testid="source-search" />
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-4 gap-4 mb-6">
              {[
                { val: stats.total, label: 'Total Sources', color: '#0b1c30' },
                { val: stats.api_total, label: 'API Integrations', color: '#004ac6' },
                { val: stats.categories?.length, label: 'Categories', color: '#0b1c30' },
                { val: stats.priorities?.find(p => p.name === 'Critical')?.count || 0, label: 'Critical Sources', color: '#ef4444' },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm p-5">
                  <p className="text-2xl font-extrabold tracking-tight" style={{ color: s.color }}>{s.val}</p>
                  <p className="text-[10px] text-[#434655] uppercase tracking-widest mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Health Monitor */}
          <div className="bg-white rounded-xl shadow-sm p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight">API Source Health Monitor</h2>
                <p className="text-[10px] text-[#434655]">Ping {stats?.api_total || 62} API sources to check availability</p>
              </div>
              <button onClick={runHealthCheck} disabled={healthLoading} data-testid="run-health-check"
                className="px-4 py-2 bg-[#004ac6] text-white text-xs font-bold rounded-lg hover:bg-[#003aa0] transition-all disabled:opacity-50">
                {healthLoading ? 'Scanning...' : 'Run Health Check'}
              </button>
            </div>
            {healthData && (
              <>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div className="bg-[#f8f9ff] p-3 rounded-lg text-center"><p className="text-xl font-bold text-[#0b1c30]">{healthData.total}</p><p className="text-[10px] text-[#434655] uppercase">Scanned</p></div>
                  <div className="bg-green-50 p-3 rounded-lg text-center"><p className="text-xl font-bold text-green-600">{healthData.online}</p><p className="text-[10px] text-green-700 uppercase">Online</p></div>
                  <div className="bg-red-50 p-3 rounded-lg text-center"><p className="text-xl font-bold text-red-600">{healthData.offline}</p><p className="text-[10px] text-red-700 uppercase">Issues</p></div>
                  <div className="bg-[#0b1c30] p-3 rounded-lg text-center"><p className="text-xl font-bold text-[#004ac6]">{healthData.uptime_pct}%</p><p className="text-[10px] text-white/60 uppercase">Uptime</p></div>
                </div>
                <div className="max-h-48 overflow-y-auto grid grid-cols-2 gap-1.5">
                  {healthData.sources.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#f8f9ff]">
                      <div className={`w-2 h-2 rounded-full ${statusDot(s.status)}`} />
                      <span className="text-xs text-[#0b1c30] truncate flex-1">{s.name}</span>
                      {s.latency_ms && <span className="text-[9px] text-[#434655]">{s.latency_ms}ms</span>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={() => { setSelectedCat(null); setApiOnly(false); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${!selectedCat && !apiOnly ? 'bg-[#004ac6] text-white' : 'bg-white text-[#434655] border border-[#c3c6d7]/40 hover:bg-[#eff4ff]'}`}>All</button>
            <button onClick={() => setApiOnly(!apiOnly)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${apiOnly ? 'bg-[#004ac6] text-white' : 'bg-white text-[#434655] border border-[#c3c6d7]/40 hover:bg-[#eff4ff]'}`}>API Only</button>
            {stats?.categories?.map(cat => (
              <button key={cat.name} onClick={() => setSelectedCat(selectedCat === cat.name ? null : cat.name)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${selectedCat === cat.name ? 'bg-[#004ac6] text-white' : 'bg-white text-[#434655] border border-[#c3c6d7]/40 hover:bg-[#eff4ff]'}`}>
                {cat.name.split(':')[0].split('&')[0].trim()} ({cat.count})
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-[#0b1c30] text-white flex justify-between items-center">
              <div><span className="text-xs font-bold uppercase tracking-widest">Intelligence Sources</span><span className="text-[10px] text-white/50 ml-3">{sources.length} loaded</span></div>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              <table className="w-full text-left">
                <thead className="bg-[#f8f9ff] sticky top-0">
                  <tr>
                    <th className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest text-[#434655]">Source</th>
                    <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-[#434655]">Category</th>
                    <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-[#434655]">Type</th>
                    <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-[#434655]">API</th>
                    <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-[#434655]">Priority</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#c3c6d7]/20">
                  {loading ? (
                    <tr><td colSpan={5} className="px-5 py-8 text-center text-[#434655] text-xs">Loading sources...</td></tr>
                  ) : sources.map((s, i) => (
                    <tr key={i} className="hover:bg-[#f8f9ff] transition-all">
                      <td className="px-5 py-3">
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#0b1c30] hover:text-[#004ac6] transition-colors">{s.name}</a>
                        <p className="text-[10px] text-[#434655] mt-0.5 line-clamp-1">{s.description}</p>
                      </td>
                      <td className="px-4 py-3"><span className="text-[10px] text-[#434655]">{s.category?.split(':')[0].split('&')[0].trim()}</span></td>
                      <td className="px-4 py-3"><span className="text-[10px] text-[#434655]">{s.source_type}</span></td>
                      <td className="px-4 py-3"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${apiColor(s.api_access)}`}>{s.api_access}</span></td>
                      <td className="px-4 py-3"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${priorityColor(s.priority)}`}>{s.priority}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <LeadCaptureCTA source_page="research-database" headline="Need a custom source added?" subline="We continually expand the database — tell us which regulatory bodies, trade associations, or proprietary data feeds matter for your mandate and we'll prioritize integration." />
        </div>
      </div>
    </PublicShell>
  );
}
